import React from 'react';
import AdminMfaModal from './AdminMfaModal';
import useAdminMfaChallenge from '~/hooks/useAdminMfaChallenge';
import { useAppSelector } from '~/redux/hooks';

type Props = {
    isShow: boolean,
    onClose: () => void
}

/**
 * Formats a countdown as m:ss, or undefined once it has run out.
 *
 * Undefined rather than '0:00' so a spent clock can be left off the screen
 * entirely — a label reading "Expires in 0:00" is worse than no label, and
 * '0:00' is truthy, so the callers could not tell the two apart.
 *
 * @param {Number} seconds seconds remaining
 * @returns {String|undefined} the formatted time, or undefined at zero
 */
const formatCountdown = (seconds: number): string | undefined => {
    if (seconds <= 0) return undefined;

    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;

    return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

/**
 * The admin verification step, as a modal over the Admin page rather than a
 * page of its own.
 *
 * Mounted by withAdminSideBar, so it only ever exists on an Admin screen and
 * only for a member the server has confirmed may enter Admin. Nothing here
 * navigates: the member stays exactly where they were, and on success the page
 * renders underneath — already the one they asked for.
 *
 * The challenge request fires on mount, from useAdminMfaChallenge — which is
 * why this must not be mounted while the session is still valid or unresolved.
 *
 * Whether it is *shown* is the layout's decision, not this component's, so that
 * closing it can leave a Verify me button in the page behind. Being hidden and
 * being unmounted are deliberately different here: hidden keeps the outstanding
 * challenge and both its countdowns alive, so reopening picks up the code
 * already in the member's inbox instead of asking for another one.
 *
 * @param {Boolean} isShow whether the modal is on screen
 * @param {Function} onClose called when the member closes it
 * @returns {JSX.Element|null} the modal, or null when no verification is due
 */
export default function AdminMfaModalContainer({ isShow, onClose }: Props): JSX.Element | null {
    const { status } = useAppSelector(({ adminMfa }) => adminMfa);

    const {
        code,
        codeLength,
        maskedEmail,
        devCode,
        errorMessage,
        resendMessage,
        attemptsRemaining,
        resendsRemaining,
        isResendBlocked,
        isVerifyBlocked,
        blockedSeconds,
        isVerifying,
        isSending,
        cooldownSeconds,
        codeExpiresInSeconds,
        isCodeExpired,
        isResendDisabled,
        onChange,
        onVerify,
        onResendCode
    } = useAdminMfaChallenge(isShow);

    // No auto-submit on the last digit. Verify is the only way to send a code.
    //
    // Submitting the moment the sixth digit landed spent an attempt on a code
    // the member had not finished checking -- a mistyped or half-pasted digit
    // was gone before they could look at it, and there are only three tries
    // before an hour-long lockout. The button is disabled until the code is
    // complete, so the affordance is unchanged; only the trigger is.
    if (status !== 'required') return null;

    return (
        <AdminMfaModal
            isShow={isShow}
            code={code}
            codeLength={codeLength}
            maskedEmail={maskedEmail}
            devCode={devCode}
            errorMessage={errorMessage}
            resendMessage={resendMessage}
            attemptsRemaining={attemptsRemaining}
            resendsRemaining={resendsRemaining}
            isResendBlocked={isResendBlocked}
            isVerifyBlocked={isVerifyBlocked}
            blockedLabel={formatCountdown(blockedSeconds)}
            isVerifying={isVerifying}
            isSending={isSending}
            isResendDisabled={isResendDisabled}
            isCodeExpired={isCodeExpired}
            cooldownLabel={formatCountdown(cooldownSeconds)}
            codeExpiryLabel={formatCountdown(codeExpiresInSeconds)}
            onChange={onChange}
            onVerify={onVerify}
            onResend={onResendCode}
            onClose={onClose} />
    );
}
